import ServiceType from "@olivahealth/graphql-server/src/domain/value-objects/ServiceType";
import { useUserData } from "../services/contexts/UserDataContext";

interface UseHasOlivaAtWorkAccessResult {
  hasAccess: boolean;
  isLoading: boolean;
}

/**
 * Hook to check if the current user has access to Oliva at Work features.
 *
 * Access is granted when one of:
 * - The user's organization has the OLIVA_AT_WORK service (full access)
 * - The user's organization has OLIVA_AT_WORK_ADMIN_ONLY service AND user has ORGANISATION_ADMIN role
 *
 * @returns Object with:
 *   - hasAccess: boolean indicating if user has access to /work features
 *   - isLoading: boolean indicating if user data is still loading
 */
export default function useHasOlivaAtWorkAccess(): UseHasOlivaAtWorkAccessResult {
  const { data: user, status } = useUserData();

  const isLoading = status === "loading" || status === "initial";

  // During loading, we don't know access state yet
  if (isLoading) {
    return { hasAccess: false, isLoading: true };
  }

  const services = user?.organisation?.plan?.services ?? [];

  // Full access: OLIVA_AT_WORK service grants access to all users
  const hasFullAccess = services.includes(ServiceType.OLIVA_AT_WORK);
  if (hasFullAccess) {
    return { hasAccess: true, isLoading: false };
  }

  // Admin-only access: OLIVA_AT_WORK_ADMIN_ONLY requires ORGANISATION_ADMIN role
  const hasAdminOnlyService = services.includes(
    ServiceType.OLIVA_AT_WORK_ADMIN_ONLY,
  );
  if (hasAdminOnlyService) {
    const isOrgAdmin = user?.role?.includes("ORGANISATION_ADMIN") ?? false;
    return { hasAccess: isOrgAdmin, isLoading: false };
  }

  return { hasAccess: false, isLoading: false };
}
